/**
 * Which page a visit entered on and where it came from — counted for every
 * visitor, because nothing about it is kept on the device or tied to a person.
 *
 * The browser decides, once per page load, whether the load is an entry
 * (`src/lib/entry.ts`): a visit from another site or typed in, not a reload
 * from our own pages and not the way back from Google's sign-in, Stripe or an
 * e-mail link. An entry is reported to `/api/entry`, which adds one to a
 * day's count for that page and source (`entry_stats`). The same two values
 * stay in the tab's memory, and the sign-in carries them across the trip
 * away from the page in the return address — `ENTRY_PARAMS` — never in
 * storage, so the `/session` that creates an account can add one to that
 * page's sign-ups. Neither count names an account: this is how many, never who.
 * Where one particular account came from is `signup-source.ts`, and that one
 * still waits for the statistics consent.
 */

/** The return address's query parameters that carry the entry across a sign-in. */
export const ENTRY_PARAMS = { path: "entry", source: "entry_src" } as const;

export interface Entry {
  /** The path as visited, language prefix included: `/uk/telc`. */
  path: string;
  /** `utm_source` if the link had one, else the other site, else `direct`. */
  source: string;
}

/** Pages whose name could not be read, or whose visit came before the counting. */
export const UNKNOWN = "(невідомо)";
/** A path that is no page of ours, or a source that is not a plain name. */
export const OTHER = "(інше)";

/** Hosts a sign-in or a payment sends people back from — a step, not a source. */
export const PASS_THROUGH = /(^|\.)(firebaseapp\.com|accounts\.google\.com|stripe\.com)$/;

/** Many hosts, one source: Google answers from a domain per country. */
const SOURCES: [RegExp, string][] = [
  [/(^|\.)google\.[a-z.]+$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)yandex\.[a-z.]+$/, "yandex"],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me)$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)(t\.co|x\.com|twitter\.com)$/, "x"],
  [/(^|\.)reddit\.com$/, "reddit"],
  [/(^|\.)(chatgpt\.com|openai\.com)$/, "chatgpt"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "youtube"],
  [/(^|\.)(t\.me|telegram\.org)$/, "telegram"],
];

const NAME = /^[a-z0-9][a-z0-9._-]{0,59}$/;

/** A source as one short lower-case name, so the count does not split on spelling. */
export function sourceName(utmSource: string | null | undefined, referrerHost: string | null | undefined): string {
  const tag = utmSource?.trim().toLowerCase();
  if (tag) return NAME.test(tag) ? tag : OTHER;
  const host = referrerHost?.trim().toLowerCase().replace(/^(www|m|l|lm|mobile)\./, "");
  if (!host) return "direct";
  for (const [pattern, name] of SOURCES) if (pattern.test(host)) return name;
  return NAME.test(host) ? host : OTHER;
}

/** `/uk/telc` → `/telc`: the route table never sees the language. */
export function withoutLocale(path: string): string {
  const m = /^\/(uk|ru|en)(\/.*)?$/.exec(path);
  return m ? (m[2] ?? "/") : path;
}

/**
 * The path as counted: one of `known` (with its language prefix, which says
 * something), or `OTHER`. Anything else is a URL somebody typed, a page that
 * does not exist, or one of an account's own (`/result/…`), and a count keyed
 * by it would grow without end.
 */
export function entryPath(path: string, known: ReadonlySet<string>): string {
  const clean = path.split(/[?#]/)[0]!.replace(/\/+$/, "") || "/";
  return known.has(withoutLocale(clean)) ? clean : OTHER;
}

/** A path as `entryPath` leaves it: lower case, letters, digits and dashes. */
const PATH = /^\/[a-z0-9/-]{0,120}$/;

/**
 * The body's `entry`, or null. The browser's word, so text of a fixed shape
 * and nothing else; which pages exist is the browser's to know (`entryPath`,
 * against the route table the Worker does not bundle).
 */
export function asEntry(value: unknown): Entry | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.path !== "string" || typeof v.source !== "string") return null;
  const path = v.path.trim();
  const source = v.source.trim().toLowerCase();
  return {
    path: path === OTHER || PATH.test(path) ? path : OTHER,
    source: source === OTHER || NAME.test(source) ? source : OTHER,
  };
}

/** Crawlers that run scripts still say who they are; one that does not is not counted against. */
export const BOT = /bot|crawl|spider|slurp|preview|facebookexternalhit|headless|lighthouse|python|curl|wget|httpclient|okhttp|go-http|axios|node-fetch|bytespider|semrush|ahrefs/i;

/** The UTC day a count belongs to. */
export const dayOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
